/**
 * ParcelExhibits — Parcel Research: the proof behind a finding (2026-10-26, AT-PZ6S6).
 *
 * An exhibit is one image or one PDF page (a CAL FIRE FHSZ map, the city zoning map, a FEMA
 * FIRMette) tied to the finding it verifies. `ExhibitStrip` sits right under that finding in the
 * Research Tool (Zone, Special Overlay, each hazard); `ExhibitsPane` is the tool's Exhibits tab,
 * every exhibit grouped by finding, plus "Other". The Pre-Design report marks each finding
 * "see Exhibit N" and prints the exhibits at the end.
 *
 * Images are pasted, dropped or chosen and stored as PNG. A multi-page PDF opens the page picker
 * so only the page that proves the finding is stored. Data: services/parcels.js (Supabase
 * `parcel_exhibits`, database/349). See docs/PARCEL_RESEARCH.md "Exhibits".
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { confirmDialog } from './Dialog.jsx'
import { usePdfPagePicker } from './PdfPagePickerModal.jsx'
import {
  EXHIBIT_TOPICS, getParcelExhibits, addParcelExhibit, updateParcelExhibit, removeParcelExhibit,
} from '../services/parcels.js'

/** Loads a parcel's exhibits once; strips and the pane share the list. */
export function useParcelExhibits(parcelId) {
  const [exhibits, setExhibits] = useState(null)   // null = loading
  const [loadError, setLoadError] = useState('')
  useEffect(() => {
    let live = true
    setExhibits(null); setLoadError('')
    if (!parcelId) return
    getParcelExhibits(parcelId)
      .then(list => { if (live) setExhibits(list) })
      .catch(() => { if (live) { setExhibits([]); setLoadError('Could not load the exhibits.') } })
    return () => { live = false }
  }, [parcelId])
  return { exhibits, setExhibits, loadError }
}

/**
 * The exhibits for ONE finding, under that finding's row. Empty = one quiet "Attach proof" link,
 * so the research form doesn't grow a drop zone under every line.
 */
export function ExhibitStrip({ parcelId, topic, store, memberName, alwaysOpen = false, inline = true }) {
  const { exhibits, setExhibits } = store
  const mine = (exhibits || []).filter(e => e.topic === topic)
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const fileRef = useRef(null)
  const { resolveUpload, picker } = usePdfPagePicker()
  const t = EXHIBIT_TOPICS.find(x => x.key === topic)

  const upload = useCallback(async files => {
    const list = [...(files || [])].filter(Boolean)
    if (!list.length) return
    setBusy(true); setError('')
    try {
      for (const raw of list) {
        const isPdf = raw.type === 'application/pdf' || /\.pdf$/i.test(raw.name || '')
        if (!isPdf && !(raw.type || '').startsWith('image/')) throw new Error('Exhibits are images or PDFs.')
        const file = await resolveUpload(raw)
        if (!file) continue   // cancelled the page picker
        const ex = await addParcelExhibit(parcelId, file, { topic, byName: memberName })
        setExhibits(prev => [...(prev || []), ex])
      }
      setOpen(false)
    } catch (e) {
      setError(e.message || 'Could not attach that file.')
    }
    setBusy(false)
  }, [parcelId, topic, memberName, resolveUpload, setExhibits])

  function onPaste(e) {
    const files = [...(e.clipboardData ? e.clipboardData.items : [])].filter(i => i.kind === 'file').map(i => i.getAsFile())
    if (files.length) { e.preventDefault(); upload(files) }
  }

  const showDrop = alwaysOpen || open || busy
  if (exhibits === null) return null
  return (
    <div style={inline ? S.strip : S.stripFlat}>
      {picker}
      {mine.map(ex => <ExhibitCard key={ex.id} ex={ex} store={store} />)}
      {showDrop ? (
        <div tabIndex={0} style={{ ...S.drop, opacity: busy ? 0.6 : 1 }} onPaste={onPaste}
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); upload(e.dataTransfer.files) }}>
          {busy ? 'Attaching…' : (<>
            Click here and paste a screenshot (Ctrl+V), drop an image or PDF, or{' '}
            <button style={S.linkBtn} onClick={() => fileRef.current && fileRef.current.click()}>choose a file</button>.
            {!alwaysOpen && <button style={{ ...S.linkBtn, marginLeft: 10, color: 'var(--aj-text-muted)' }} onClick={() => setOpen(false)}>Cancel</button>}
          </>)}
        </div>
      ) : (
        <button style={S.attachBtn} onClick={() => setOpen(true)}>
          + {mine.length ? 'Attach another' : `Attach proof${t && t.key !== 'other' ? ' (' + t.label.toLowerCase() + ')' : ''}`}
        </button>
      )}
      {error && <div style={S.error}>{error}</div>}
      <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple style={{ display: 'none' }}
        onChange={e => { upload(e.target.files); e.target.value = '' }} />
    </div>
  )
}

function ExhibitCard({ ex, store }) {
  const { setExhibits } = store
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  async function save(fields) {
    const changed = Object.keys(fields).some(k => (ex[k] || '') !== (fields[k] || ''))
    if (!changed) return
    setSaving(true); setError('')
    try {
      const next = await updateParcelExhibit(ex.id, fields)
      setExhibits(prev => (prev || []).map(e => e.id === ex.id ? next : e))
    } catch { setError('Could not save.') }
    setSaving(false)
  }
  async function remove() {
    if (!(await confirmDialog({ title: 'Remove this exhibit?', message: 'The report will no longer show it.', confirmLabel: 'Remove exhibit', danger: true }))) return
    try {
      await removeParcelExhibit(ex.id)
      setExhibits(prev => (prev || []).filter(e => e.id !== ex.id))
    } catch { setError('Could not remove.') }
  }
  return (
    <div style={S.card}>
      <a href={ex.url} target="_blank" rel="noopener noreferrer" style={S.thumbLink} title="Open full size">
        {ex.kind === 'image'
          ? <img src={ex.url} alt={ex.caption || 'Exhibit'} style={S.thumb} />
          : <div style={S.pdfThumb}>PDF</div>}
      </a>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <input key={ex.id + 'cap'} style={S.input} defaultValue={ex.caption}
          placeholder="Caption, e.g. CAL FIRE FHSZ map, parcel outlined in red"
          onBlur={e => save({ caption: e.target.value.trim() })} />
        <input key={ex.id + 'src'} style={S.input} defaultValue={ex.source}
          placeholder="Source, e.g. CAL FIRE FHSZ Viewer, viewed 10/26/2026, or a link"
          onBlur={e => save({ source: e.target.value.trim() })} />
        <div style={S.cardFoot}>
          <span>{ex.fileName}{ex.addedBy ? ' · added by ' + ex.addedBy : ''}</span>
          <select value={ex.topic} style={S.topicSel} title="The finding this exhibit verifies"
            onChange={e => save({ topic: e.target.value })}>
            {EXHIBIT_TOPICS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <button style={{ ...S.linkBtn, color: '#c0392b' }} onClick={remove}>Remove</button>
          {saving && <span>saving…</span>}
          {error && <span style={{ color: '#c0392b' }}>{error}</span>}
        </div>
      </div>
    </div>
  )
}

/** The Exhibits tab: every exhibit grouped by the finding it verifies. */
export function ExhibitsPane({ parcelId, store, memberName }) {
  const { exhibits, loadError } = store
  if (exhibits === null) return <div style={S.muted}>Loading exhibits…</div>
  const count = exhibits.length
  return (
    <div style={{ maxWidth: 720 }}>
      <div style={S.intro}>
        Attach the map or document that proves each finding. The Pre-Design report marks the finding
        "see Exhibit N" and prints the exhibits at the end, in this order. You can also attach under
        each finding on the Zoning and Hazards tabs.
        {count > 0 && <span style={S.count}>{count} exhibit{count === 1 ? '' : 's'}</span>}
      </div>
      {loadError && <div style={S.error}>{loadError}</div>}
      {EXHIBIT_TOPICS.map(t => {
        const has = exhibits.some(e => e.topic === t.key)
        return (
          <div key={t.key} style={S.group}>
            <div style={S.groupHead}>
              {t.label}
              {t.finding && <span style={S.groupSub}>verifies {t.finding}</span>}
            </div>
            <ExhibitStrip parcelId={parcelId} topic={t.key} store={store} memberName={memberName} alwaysOpen={t.key === 'other' && !has} inline={false} />
          </div>
        )
      })}
    </div>
  )
}

const S = {
  strip: { maxWidth: 720, padding: '4px 0 10px 274px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 8, borderBottom: '0.5px solid var(--aj-border)' },
  stripFlat: { display: 'flex', flexDirection: 'column', gap: 8, padding: '4px 0 8px' },
  card: { display: 'flex', gap: 12, padding: 10, border: '0.5px solid var(--aj-border-mid)', borderRadius: 10, background: 'var(--aj-bg-surface)' },
  thumbLink: { flex: '0 0 auto', display: 'block' },
  thumb: { width: 120, height: 90, objectFit: 'cover', borderRadius: 4, border: '0.5px solid var(--aj-border)', display: 'block', background: '#fff' },
  pdfThumb: { width: 120, height: 90, borderRadius: 4, border: '0.5px solid var(--aj-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '"DM Mono",monospace', fontSize: 13, color: 'var(--aj-text-mid)', background: 'var(--aj-bg-subtle)' },
  input: { width: '100%', boxSizing: 'border-box', fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '6px 8px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)' },
  cardFoot: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 11, color: 'var(--aj-text-muted)', flexWrap: 'wrap' },
  topicSel: { fontFamily: '"DM Sans",sans-serif', fontSize: 11, padding: '3px 6px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', marginLeft: 'auto' },
  drop: { border: '0.5px dashed var(--aj-border-mid)', borderRadius: 4, padding: '14px 12px', fontSize: 12, color: 'var(--aj-text-mid)', background: 'var(--aj-bg-subtle)', outline: 'none' },
  attachBtn: { alignSelf: 'flex-start', border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--aj-accent-blue, #1d6fa5)', fontSize: 12, padding: 0, fontFamily: '"DM Sans",sans-serif' },
  linkBtn: { border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--aj-accent-blue, #1d6fa5)', fontSize: 12, padding: 0, fontFamily: '"DM Sans",sans-serif', textDecoration: 'underline' },
  error: { color: '#c0392b', fontSize: 12 },
  muted: { color: 'var(--aj-text-muted)', fontSize: 13, padding: '8px 0' },
  intro: { fontSize: 12, color: 'var(--aj-text-mid)', lineHeight: 1.5, marginBottom: 14 },
  count: { marginLeft: 8, fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'var(--aj-text-muted)' },
  group: { marginBottom: 10 },
  groupHead: { fontSize: 13, fontWeight: 500, color: 'var(--aj-text)', padding: '8px 0 2px' },
  groupSub: { marginLeft: 8, fontSize: 11, fontWeight: 400, color: 'var(--aj-text-muted)' },
}
